import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'contexts/ThemeContext';
import { getLikesSettingsLogs } from 'services/actions/likesService';
import { ArrowUpDown, Inbox, Loader2 } from 'lucide-react';
import Spinner from 'components/common/Spinner';

interface LikesLogsProps {
  selectedUser: string;
}

const PAGE_SIZE = 50;

const LikesLogs: React.FC<LikesLogsProps> = ({ selectedUser }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const cellTextColor = isDark ? '#CAD5E2' : '#45556C';
  const linkColor = isDark ? '#51A2FF' : '#2B7FFF';
  const tableBorder = isDark ? '#1D293D' : '#F1F5F9';
  const inputBg = isDark ? '#0F172B' : '#FFFFFF';
  const inputBorder = isDark ? '#314158' : '#E2E8F0';
  const inputText = isDark ? '#E2E8F0' : '#314158';
  const inputIcon = isDark ? '#62748E' : '#90A1B9';

  const isMountedRef = useRef(true);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const isLoadingRef = useRef(false);
  useEffect(() => { return () => { isMountedRef.current = false; }; }, []);

  const fetchLogs = useCallback(async (pageNum: number, silent = false, append = false) => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    try {
      if (!silent && !append) { setLoading(true); setError(null); }
      if (append) setLoadingMore(true);
      const dateParam = selectedDate !== 'all' ? selectedDate : undefined;
      const data: any = await getLikesSettingsLogs(selectedUser, pageNum, PAGE_SIZE, dateParam);
      if (isMountedRef.current) {
        const items = data.data || data;
        const pagination = data.pagination;
        if (append) {
          setLogs(prev => [...prev, ...items]);
        } else {
          setLogs(Array.isArray(items) ? items : []);
        }
        if (pagination) {
          setHasMore(pagination.page < pagination.totalPages);
        } else {
          setHasMore(false);
        }
      }
    } catch (error: any) {
      if (isMountedRef.current && !silent) {
        console.error('Error fetching logs:', error.message);
        setError(t('likesLogs.errors.loadFailed'));
      }
    } finally {
      isLoadingRef.current = false;
      if (isMountedRef.current) {
        if (!silent && !append) setLoading(false);
        if (append) setLoadingMore(false);
      }
    }
  }, [selectedUser, selectedDate, t]);

  // Initial fetch & reset on user/date change
  useEffect(() => {
    if (selectedUser) {
      setPage(1);
      setHasMore(true);
      setLogs([]);
      fetchLogs(1);
    }
  }, [selectedUser, selectedDate, fetchLogs]);

  // Silent auto-refresh every 30 seconds (refreshes page 1 only)
  useEffect(() => {
    if (!selectedUser) return;
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible' && !isLoadingRef.current) {
        // Silently refresh - reload all current pages
        const dateParam = selectedDate !== 'all' ? selectedDate : undefined;
        getLikesSettingsLogs(selectedUser, 1, page * PAGE_SIZE, dateParam)
          .then((data: any) => {
            if (isMountedRef.current) {
              const items = data.data || data;
              const pagination = data.pagination;
              setLogs(Array.isArray(items) ? items : []);
              if (pagination) {
                setHasMore(pagination.page < pagination.totalPages);
              }
            }
          })
          .catch(() => {});
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [selectedUser, selectedDate, page]);

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingRef.current && !loading) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchLogs(nextPage, false, true);
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => { observerRef.current?.disconnect(); };
  }, [hasMore, page, loading, fetchLogs]);

  const generateShortcode = (mediaId: string): string => {
    try {
      const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
      let id = BigInt(mediaId); let shortcode = "";
      while (id > 0n) { const remainder = id % 64n; shortcode = alphabet[Number(remainder)] + shortcode; id = id / 64n; }
      return shortcode;
    } catch (e) { return 'unknown'; }
  };

  const formatDate = (dateStr: string) => {
    try { return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); } catch { return dateStr; }
  };

  const formatTime = (dateStr: string) => {
    try { return new Date(dateStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true }); } catch { return ''; }
  };

  const last30Days = Array.from({ length: 30 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - i);
    return d.toISOString().split('T')[0];
  });

  const sortedLogs = [...logs].sort((a, b) => {
    const dateA = new Date(a.created_at || 0).getTime();
    const dateB = new Date(b.created_at || 0).getTime();
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });


  if (loading) return <Spinner size="large" text={t('likesLogs.loading')} fullPage />;

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center" style={{ padding: '48px', textAlign: 'center' }}>
        <p style={{ fontSize: '14px', color: cellTextColor }}>{error}</p>
        <button style={{ marginTop: '16px', padding: '6px 14px', background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 500, color: '#FFFFFF' }} onClick={() => window.location.reload()}>
          {t('likesLogs.buttons.retry')}
        </button>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center" style={{ padding: '64px 48px', textAlign: 'center', gap: '12px' }}>
        <div className="flex items-center justify-center" style={{ width: '48px', height: '48px', borderRadius: '12px', background: isDark ? '#1D293D' : '#F1F5F9' }}>
          <Inbox size={24} color={isDark ? '#62748E' : '#90A1B9'} />
        </div>
        <p style={{ fontSize: '14px', fontWeight: 500, color: cellTextColor }}>{t('likesLogs.emptyState.text', 'No logs available')}</p>
      </div>
    );
  }

  return (
    <>
      <div style={{ position: 'relative' }}>
        <select value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={{ width: '100%', height: '32px', padding: '6px 10px', background: inputBg, border: `1px solid ${inputBorder}`, borderRadius: '8px', fontSize: '14px', lineHeight: '20px', fontWeight: 400, color: inputText, appearance: 'none', cursor: 'pointer', outline: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(inputIcon)}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center', backgroundSize: '20px', paddingRight: '36px' }}>
          <option value="all">{t('likesLogs.filters.allDates', 'All Dates')}</option>
          {last30Days.map((date) => (<option key={date} value={date}>{formatDate(date)}</option>))}
        </select>
      </div>

      <div style={{ border: `1px solid ${tableBorder}`, borderRadius: '8px', overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: '600px', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ padding: '6px 12px 6px 6px', background: mainBg, borderBottom: `1px solid ${tableBorder}`, textAlign: 'left' }}>
                <button className="flex items-center" style={{ padding: '6px 10px', gap: '4px', background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: '8px' }} onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}>
                  <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: titleColor }}>{t('likesLogs.table.date', 'Date')}</span>
                  <ArrowUpDown size={20} color={titleColor} />
                </button>
              </th>
              <th style={{ padding: '12px 16px', background: mainBg, borderBottom: `1px solid ${tableBorder}`, textAlign: 'left' }}>
                <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: titleColor }}>{t('likesLogs.table.time', 'Time')}</span>
              </th>
              <th style={{ padding: '12px 16px', background: mainBg, borderBottom: `1px solid ${tableBorder}`, textAlign: 'left' }}>
                <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: titleColor }}>{t('likesLogs.table.rule', 'Rule')}</span>
              </th>
              <th style={{ padding: '12px 16px', background: mainBg, borderBottom: `1px solid ${tableBorder}`, textAlign: 'left' }}>
                <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: titleColor }}>{t('likesLogs.table.target', 'Target')}</span>
              </th>
              <th style={{ padding: '12px 16px', background: mainBg, borderBottom: `1px solid ${tableBorder}`, textAlign: 'left' }}>
                <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: titleColor }}>{t('likesLogs.table.post', 'Post')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedLogs.map((log, index) => (
              <tr key={index}>
                <td style={{ padding: '12px 16px', background: mainBg, borderBottom: `1px solid ${tableBorder}` }}>
                  <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 400, color: cellTextColor }}>{log.created_at ? formatDate(log.created_at) : '—'}</span>
                </td>
                <td style={{ padding: '12px 16px', background: mainBg, borderBottom: `1px solid ${tableBorder}` }}>
                  <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 400, color: cellTextColor }}>{log.created_at ? formatTime(log.created_at) : '—'}</span>
                </td>
                <td style={{ padding: '12px 16px', background: mainBg, borderBottom: `1px solid ${tableBorder}` }}>
                  <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 400, color: cellTextColor }}>{log.rule_info?.rule_name || t('likesLogs.labels.unknown', 'Rule Name')}</span>
                </td>
                <td style={{ padding: '12px 16px', background: mainBg, borderBottom: `1px solid ${tableBorder}` }}>
                  <a href={`https://www.instagram.com/${log.username}/`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: linkColor, textDecoration: 'none' }}>
                    @{log.username || t('likesLogs.labels.unknown')}
                  </a>
                </td>
                <td style={{ padding: '12px 16px', background: mainBg, borderBottom: `1px solid ${tableBorder}` }}>
                  {log.media_id ? (
                    <a href={`https://www.instagram.com/p/${generateShortcode(log.media_id)}/`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: linkColor, textDecoration: 'none' }}>
                      {t('likesLogs.labels.post', 'View Post')}
                    </a>
                  ) : <span style={{ fontSize: '14px', color: cellTextColor }}>—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Infinite scroll sentinel */}
      <div ref={loadMoreRef} style={{ padding: '16px', textAlign: 'center', minHeight: '1px' }}>
        {loadingMore && (
          <div className="flex items-center justify-center" style={{ gap: '8px' }}>
            <Loader2 size={18} color={cellTextColor} className="animate-spin" />
            <span style={{ fontSize: '13px', color: cellTextColor }}>{t('likesLogs.loadingMore', 'Loading more...')}</span>
          </div>
        )}
      </div>
    </>
  );
};

export default LikesLogs;
